/**
 * The design primitives. Every screen is built from these, which is what keeps
 * six pages looking like one instrument rather than six.
 *
 * Read tailwind.config.js and index.css first — the three decisions are stated
 * there (no radius, rules instead of shadows, one accent meaning act or danger)
 * and everything below is those decisions applied. What this file adds on top:
 *
 *   - A destructive action never looks like a normal one. Danger is an outline,
 *     never a fill, because a filled red button invites the same reflex click as
 *     a filled primary sitting next to it.
 *   - State is a colour AND a word, always. The word may be drawn or it may be
 *     read only by a screen reader, but it is never absent.
 *   - Anything that can be loading gets a real skeleton in the shape of the
 *     thing it stands in for. A panel that flashes empty and then fills reads as
 *     broken.
 *
 * Two colours here are not in the token set, and both come from the design file
 * rather than from a preference: the accent lightened for text on the dark
 * ground, and darkened for text on the paper field. `#ec3013` itself sits at
 * 3.4:1 against the ground and 3.7:1 against paper, which is under the floor for
 * anything set below 18px — so where the accent has to be *read* rather than
 * seen, these are what it becomes.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** The accent, raised to a legible weight on the dark ground. */
const ACCENT_INK = 'text-[#ff8d76]';
/** The accent, dropped to a legible weight on the paper field. */
const ACCENT_INK_PAPER = 'text-[#a8410e]';
/** The paper field's own hairline — the light-panel equivalent of rule-faint. */
export const PAPER_RULE = 'border-paper-ink/20';

/* ------------------------------------------------------------------ spinner */

/**
 * A square with one lit edge, turning. Not a circle: there is no radius in this
 * system, and a spinner is the one place a stray one would be forgiven and then
 * copied.
 */
export function Spinner({ className, tone = 'dark' }: { className?: string; tone?: 'dark' | 'paper' }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-block shrink-0 animate-spin border-2',
        tone === 'paper' ? 'border-paper-ink/25 border-t-paper-ink' : 'border-ink-disabled border-t-ink-primary',
        className ?? 'h-3 w-3',
      )}
    />
  );
}

/* ------------------------------------------------------------------ button */

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
/** 40 / 44 / 48 / 52px. The design's four weights, and nothing between them. */
type Size = 'sm' | 'md' | 'lg' | 'xl';

/**
 * On the dark ground the strongest button is the light one: a paper fill against
 * near-black is the loudest thing this palette can say, and it leaves the accent
 * free to mean "irreversible" wherever it appears. The accent still arrives on
 * hover, at the moment the pointer has committed and the label has been read.
 */
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-paper text-paper-ink hover:bg-accent hover:text-paper',
  secondary: 'border-2 border-rule-control text-ink-primary hover:border-ink-secondary',
  // Outlined, always. The border carries the warning at rest; a fill here would
  // put a red button exactly where a safe one usually is.
  danger: cx('border-2 border-accent hover:bg-accent/10', ACCENT_INK),
  ghost: 'text-ink-secondary hover:text-ink-primary',
};

const VARIANTS_DISABLED: Record<Variant, string> = {
  primary: 'bg-ground-panel text-ink-tertiary',
  secondary: 'border-2 border-rule-faint text-ink-tertiary',
  danger: 'border-2 border-rule-faint text-ink-tertiary',
  ghost: 'text-ink-disabled',
};

const SIZES: Record<Size, string> = {
  sm: 'h-10 px-3.5 text-sm',
  md: 'h-11 px-[18px] text-[14px]',
  lg: 'h-12 px-5 text-base',
  xl: 'h-[52px] px-5 text-[16px]',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  /** Sits after the label — the arrow on a "Set up this server →" call to action. */
  trailingIcon?: ReactNode;
  /** Full width, label flush left and the trailing icon flush right. */
  block?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  trailingIcon,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const inert = disabled === true || loading;
  return (
    <button
      {...rest}
      disabled={inert}
      className={cx(
        // whitespace-nowrap: a label that wraps breaks the fixed height every
        // size here depends on.
        'inline-flex select-none items-center gap-2.5 whitespace-nowrap font-bold transition-colors duration-150',
        block ? 'w-full justify-between' : 'justify-center',
        inert ? cx(VARIANTS_DISABLED[variant], 'cursor-not-allowed') : VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading ? <Spinner className="h-3.5 w-3.5" tone={variant === 'primary' ? 'paper' : 'dark'} /> : icon}
      {children}
      {trailingIcon}
    </button>
  );
}

/**
 * The same button with the polarity inverted, for use inside `.paper-field`.
 *
 * A dark-ground button dropped onto the light panel disappears into it, and the
 * panel is by definition the one thing the screen wants pressed — so it gets its
 * own set rather than a `dark:` variant nobody would remember to write.
 */
const PAPER_VARIANTS: Record<Variant, string> = {
  primary: 'bg-paper-ink text-paper hover:bg-accent hover:text-ground',
  secondary: 'border-2 border-paper-ink text-paper-ink hover:bg-paper-ink hover:text-paper',
  danger: cx('border-2 border-accent hover:bg-accent/10', ACCENT_INK_PAPER),
  ghost: 'text-paper-secondary hover:text-paper-ink',
};

const PAPER_VARIANTS_DISABLED: Record<Variant, string> = {
  primary: cx('border-2 text-paper-tertiary', PAPER_RULE),
  secondary: cx('border-2 text-paper-tertiary', PAPER_RULE),
  danger: cx('border-2 text-paper-tertiary', PAPER_RULE),
  ghost: 'text-paper-tertiary',
};

export function PaperButton({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  trailingIcon,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const inert = disabled === true || loading;
  return (
    <button
      {...rest}
      disabled={inert}
      className={cx(
        'inline-flex select-none items-center gap-2.5 whitespace-nowrap font-bold transition-colors duration-150',
        block ? 'w-full justify-between' : 'justify-center',
        inert ? cx(PAPER_VARIANTS_DISABLED[variant], 'cursor-not-allowed') : PAPER_VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading ? <Spinner className="h-3.5 w-3.5" tone={variant === 'primary' ? 'dark' : 'paper'} /> : icon}
      {children}
      {trailingIcon}
    </button>
  );
}

/**
 * A square icon-only control, 40px even where the glyph inside it is 15px. The
 * screen with the most of these on it is also the one people reach for on a
 * phone during an incident.
 */
export function IconButton({
  label,
  onClick,
  disabled,
  tone = 'default',
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex h-10 w-10 shrink-0 items-center justify-center transition-colors',
        'disabled:cursor-not-allowed disabled:text-ink-disabled disabled:hover:bg-transparent',
        tone === 'danger'
          ? 'text-accent hover:bg-accent/10'
          : 'text-ink-tertiary hover:bg-ground-panel hover:text-ink-primary',
        className,
      )}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ status */

export type InstanceStatus =
  | 'building'
  | 'active'
  | 'suspended'
  | 'reinstalling'
  | 'deleting'
  | 'destroyed'
  | 'failed';

/**
 * One table of state, read by the dot, the badge and anything else that needs to
 * name a machine's condition.
 *
 * `settled: false` marks the states a machine leaves on its own. They are the
 * only ones that breathe — a light that moves means work is happening, and if
 * everything moved that would mean nothing.
 *
 * `ink` is not always the dot colour: the accent and the grey are both under the
 * contrast floor as small text, so where the state is written out it is written
 * in the readable neighbour of the same hue.
 */
interface StateMeta {
  label: string;
  /** The dot itself, its 25% halo, the badge's 10% field and its leading edge. */
  dot: string;
  halo: string;
  fill: string;
  edge: string;
  ink: string;
  settled: boolean;
}

/* Every class is written out rather than derived: Tailwind reads this file as
   text, and a class name assembled at runtime is a class name that is never
   generated. */
const STATE: Record<string, StateMeta> = {
  active:       { label: 'Active',       dot: 'bg-state-healthy',   halo: 'bg-state-healthy/25',   fill: 'bg-state-healthy/10',   edge: 'border-state-healthy',   ink: 'text-state-healthy',   settled: true },
  building:     { label: 'Building',     dot: 'bg-state-progress',  halo: 'bg-state-progress/25',  fill: 'bg-state-progress/10',  edge: 'border-state-progress',  ink: 'text-accent-link',     settled: false },
  reinstalling: { label: 'Reinstalling', dot: 'bg-state-progress',  halo: 'bg-state-progress/25',  fill: 'bg-state-progress/10',  edge: 'border-state-progress',  ink: 'text-accent-link',     settled: false },
  deleting:     { label: 'Deleting',     dot: 'bg-state-attention', halo: 'bg-state-attention/25', fill: 'bg-state-attention/10', edge: 'border-state-attention', ink: 'text-state-attention', settled: false },
  suspended:    { label: 'Suspended',    dot: 'bg-state-attention', halo: 'bg-state-attention/25', fill: 'bg-state-attention/10', edge: 'border-state-attention', ink: 'text-state-attention', settled: true },
  failed:       { label: 'Failed',       dot: 'bg-state-bad',       halo: 'bg-state-bad/25',       fill: 'bg-state-bad/10',       edge: 'border-state-bad',       ink: ACCENT_INK,             settled: true },
  destroyed:    { label: 'Destroyed',    dot: 'bg-state-off',       halo: 'bg-state-off/25',       fill: 'bg-state-off/10',       edge: 'border-state-off',       ink: 'text-ink-mono',        settled: true },
  /* Power is a different question from state, and the server header asks both. */
  running:      { label: 'Running',      dot: 'bg-state-healthy',   halo: 'bg-state-healthy/25',   fill: 'bg-state-healthy/10',   edge: 'border-state-healthy',   ink: 'text-state-healthy',   settled: true },
  stopped:      { label: 'Stopped',      dot: 'bg-state-off',       halo: 'bg-state-off/25',       fill: 'bg-state-off/10',       edge: 'border-state-off',       ink: 'text-ink-mono',        settled: true },
  unknown:      { label: 'Unknown',      dot: 'bg-state-off',       halo: 'bg-state-off/25',       fill: 'bg-state-off/10',       edge: 'border-state-off',       ink: 'text-ink-mono',        settled: true },
};

function stateOf(status: string): StateMeta {
  return STATE[status] ?? STATE.unknown!;
}

/** The word for a state, for a caller that draws its own indicator. */
export function statusLabel(status: string): string {
  return stateOf(status).label;
}

/** The text colour for a state, for a caller drawing the label at display size. */
export function statusInk(status: string): string {
  return stateOf(status).ink;
}

const DOT_SIZES = {
  sm: { halo: 'h-2 w-2', dot: 'h-[5px] w-[5px]' },
  md: { halo: 'h-2.5 w-2.5', dot: 'h-1.5 w-1.5' },
  lg: { halo: 'h-[13px] w-[13px]', dot: 'h-2 w-2' },
} as const;

/**
 * State as a light, for the places where the word would be repetition.
 *
 * In a list of machines that are nearly all fine, spelling "Active" out on every
 * row costs a column and says nothing; the colour says it at a glance and the
 * word is still there for a screen reader and for a hover. A machine that is
 * *not* fine gets the badge as well — the exception is what deserves the words.
 *
 * The halo is not decoration: it is what gives a 6px dot enough presence to be
 * the leftmost thing on a 76px row and still be found from across a desk.
 */
export function StatusDot({
  status,
  size = 'md',
  halo = true,
  /** Off where a badge beside it already says the same word out loud. */
  announce = true,
  className,
}: {
  status: string;
  size?: keyof typeof DOT_SIZES;
  halo?: boolean;
  announce?: boolean;
  className?: string;
}) {
  const s = stateOf(status);
  const dims = DOT_SIZES[size];
  return (
    <span
      className={cx('relative inline-flex shrink-0 items-center justify-center', dims.halo, className)}
      title={s.label}
    >
      {halo ? <span aria-hidden="true" className={cx('absolute inset-0', s.halo)} /> : null}
      <span aria-hidden="true" className={cx('relative', dims.dot, s.dot, !s.settled && 'animate-breathe')} />
      {announce ? <span className="sr-only">{s.label}</span> : null}
    </span>
  );
}

/** @deprecated The name it had before the redesign. `StatusDot` is the same thing. */
export const StateDot = StatusDot;

/**
 * The state written out. Only ever drawn for a machine that is not plainly fine:
 * an "Active" badge on every row of a healthy list is what makes the one row
 * saying "Failed" harder to find.
 */
export function StatusBadge({
  status,
  size = 'sm',
  className,
}: {
  status: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const s = stateOf(status);
  return (
    <span
      className={cx(
        'inline-flex items-center whitespace-nowrap border-l-2 font-bold uppercase tracking-[0.1em]',
        size === 'md' ? 'px-2.5 py-1 text-xs' : 'px-2 py-[3px] text-2xs',
        s.fill,
        s.edge,
        s.ink,
        className,
      )}
    >
      {s.label}
    </span>
  );
}

/* ------------------------------------------------------------------ badges */

/**
 * A tag that is not machine state — a count, a provenance, a note. Same shape as
 * the status badge so the two never argue, but it carries its own words.
 */
export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'warn';
  className?: string;
}) {
  const tones = {
    neutral: 'border-rule-control bg-ground-panel text-ink-secondary',
    brand: cx('border-accent bg-accent/10', ACCENT_INK),
    warn: 'border-state-attention bg-state-attention/10 text-state-attention',
  } as const;
  return (
    <span
      className={cx(
        // tabular because a badge is very often a count ("12 / 50"), and a count
        // that reflows as it changes reads as the layout twitching.
        'inline-flex items-center whitespace-nowrap border-l-2 px-2 py-[3px] text-2xs font-bold uppercase tracking-[0.1em] tabular-nums',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * The red mono numerals that number a form's sections and a list's steps.
 * `hollow` is the outlined treatment the sign-in screen sets them at display
 * size, where a solid 34px numeral would out-shout the provider name beside it.
 */
export function IndexNumeral({
  value,
  tone = 'accent',
  variant = 'solid',
  className,
}: {
  value: number | string;
  tone?: 'accent' | 'muted';
  variant?: 'solid' | 'hollow';
  className?: string;
}) {
  const text = typeof value === 'number' ? String(value).padStart(2, '0') : value;
  return (
    <span
      aria-hidden="true"
      className={cx(
        'font-mono font-bold leading-none tabular-nums',
        variant === 'hollow' ? 'text-[34px] tracking-display' : 'text-sm',
        tone === 'accent' ? 'text-accent' : 'text-ink-tertiary',
        className,
      )}
      style={
        variant === 'hollow'
          ? { WebkitTextStroke: '1.5px currentColor', color: 'transparent' }
          : undefined
      }
    >
      {text}
    </span>
  );
}

/* ---------------------------------------------------------------- feedback */

const ALERT_TONES = {
  info: { edge: 'border-accent-link', lead: 'text-accent-link' },
  warn: { edge: 'border-state-attention', lead: 'text-state-attention' },
  error: { edge: 'border-state-bad', lead: ACCENT_INK },
  success: { edge: 'border-state-healthy', lead: 'text-state-healthy' },
} as const;

/**
 * A notice is a lit edge and a sentence, not a tinted box.
 *
 * The design's own version is edge-coded and glyph-free; the glyph is here
 * anyway because the edge is 3px of colour and the lead is coloured type, and
 * neither survives being colour-blind on a bad screen. The words still carry the
 * severity on their own — the glyph is the third channel, not the first.
 */
export function Alert({
  tone = 'error',
  title,
  children,
  className,
}: {
  tone?: keyof typeof ALERT_TONES;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const t = ALERT_TONES[tone];
  const glyphs = {
    error: 'M12 8v5M12 16.5h.01M12 3.5 2.8 19.2h18.4Z',
    warn: 'M12 8v5M12 16.5h.01M12 3.5 2.8 19.2h18.4Z',
    info: 'M12 11v6M12 7.5h.01M3 3h18v18H3Z',
    success: 'm7 12.5 3.5 3.5L18 8M3 3h18v18H3Z',
  } as const;
  return (
    <div className={cx('flex gap-3 border-l-[3px] py-1 pl-3.5', t.edge, className)} role="alert">
      <svg
        className={cx('mt-[3px] h-4 w-4 shrink-0', t.lead)}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={glyphs[tone]} />
      </svg>
      <div className="min-w-0 flex-1 text-sm leading-relaxed text-ink-secondary">
        {title ? <span className={cx('font-bold', t.lead)}>{title} </span> : null}
        {children}
      </div>
    </div>
  );
}

/**
 * The same notice on the paper field, where the dark-ground inks would vanish.
 * Used for the amber aside beside a power control and the root-password note in
 * the reinstall dialog.
 */
export function PaperNote({
  tone = 'warn',
  children,
  className,
}: {
  tone?: 'warn' | 'plain';
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cx(
        'text-xs font-semibold leading-relaxed',
        tone === 'warn' ? ACCENT_INK_PAPER : 'text-paper-secondary',
        className,
      )}
    >
      {children}
    </p>
  );
}

/* ------------------------------------------------------------------ empty */

/**
 * The mark for a screen with nothing on it: empty rack slots, drawn as the
 * design draws them — three dashed bars, and no attempt to be an illustration.
 * `occupied` fills the first N so the same mark can say "one of three" as well
 * as "none".
 */
export function RackGlyph({ occupied = 0, className }: { occupied?: number; className?: string }) {
  return (
    <div className={cx('flex flex-col gap-[7px]', className)} aria-hidden="true">
      {[0, 1, 2].map((slot) => (
        <span
          key={slot}
          className={cx(
            'block h-[30px] w-[132px] border-2',
            slot < occupied ? 'border-rule-strong bg-ground-panel' : 'border-dashed border-rule-control',
          )}
        />
      ))}
    </div>
  );
}

/**
 * The shape of "there is nothing here yet".
 *
 * Flush left like everything else, and it always ends in a sentence about what
 * happens next: an empty state that only says "nothing here" has told somebody
 * something they could already see.
 */
export function EmptyState({
  icon,
  illustration,
  title,
  description,
  action,
  footnote,
  compact = false,
  className,
}: {
  icon?: ReactNode;
  illustration?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  footnote?: ReactNode;
  /** For an empty panel inside a working page, where full height would be a hole. */
  compact?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(compact ? 'py-6' : 'py-11', className)}>
      {illustration ? <div className="mb-7">{illustration}</div> : null}
      {!illustration && icon ? <div className="mb-[18px] text-accent">{icon}</div> : null}
      <h3 className="text-2xl font-black tracking-display text-ink-primary">{title}</h3>
      {description ? (
        <p className="mt-2.5 max-w-[560px] text-base leading-relaxed text-ink-secondary">{description}</p>
      ) : null}
      {action ? <div className="mt-[22px] flex flex-wrap gap-0.5">{action}</div> : null}
      {footnote ? <p className="mt-4 max-w-[560px] text-xs text-ink-tertiary">{footnote}</p> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton', className ?? 'h-4 w-full')} aria-hidden="true" />;
}

/* -------------------------------------------------------------------- data */

/**
 * A specification list. Two tracks — label left, value hard right — so a column
 * of values reads as a column rather than as ragged sentences.
 */
export function DefinitionList({ className, children }: { className?: string; children: ReactNode }) {
  return <dl className={cx('grid grid-cols-[auto_1fr] gap-x-5', className)}>{children}</dl>;
}

/**
 * One row of a DefinitionList: a `dt`/`dd` pair on the parent grid, divided by
 * the row hairline. `mono` is for identifiers — addresses, sizes, slugs — which
 * are read character by character rather than as words.
 */
export function Field({
  label,
  value,
  mono = false,
  tone = 'default',
  title,
}: {
  label: ReactNode;
  value: ReactNode;
  mono?: boolean;
  /** `attention` is the amber row — outbound mail, and nothing else so far. */
  tone?: 'default' | 'attention';
  title?: string;
}) {
  return (
    <>
      <dt className="rule-row py-2.5 text-sm text-ink-tertiary">{label}</dt>
      <dd
        title={title}
        className={cx(
          'rule-row min-w-0 truncate py-2.5 text-right text-sm tabular-nums',
          mono ? 'font-mono text-[13px] text-ink-mono' : 'font-bold',
          tone === 'attention' ? 'text-state-attention' : mono ? 'text-ink-mono' : 'text-ink-primary',
        )}
      >
        {value}
      </dd>
    </>
  );
}

/** The design's square copy glyph, and the check it becomes. */
const COPY_PATH = 'M9 9h10v10H9zM5 15V5h10';
const CHECK_PATH = 'M4 12l5 5L20 6';

/**
 * Copy-to-clipboard for an address — the thing people copy most, usually while
 * holding a phone in the other hand.
 *
 * `prominent` is the treatment for an address that is one of the page's primary
 * objects rather than a value inside a row. Either way the click is confirmed:
 * a copy button that looks identical before and after leaves people pressing it
 * twice and pasting into a terminal to find out whether it worked.
 */
export function CopyValue({
  value,
  label,
  variant = 'inline',
  className,
}: {
  value: string;
  label?: string;
  /** `pill` is the old name for `prominent` and behaves identically. */
  variant?: 'inline' | 'prominent' | 'pill';
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  // Only a resolved write is confirmed. Where there is no clipboard at all — an
  // insecure origin, an old browser — nothing is claimed.
  const copy = () => {
    const written = navigator.clipboard?.writeText(value);
    if (!written) return;
    written.then(() => setCopied(true)).catch(() => undefined);
  };

  const glyph = (
    <svg
      className={cx('h-[15px] w-[15px] shrink-0', copied ? 'text-state-healthy' : 'text-ink-tertiary')}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={copied ? '2.2' : '1.8'}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={copied ? CHECK_PATH : COPY_PATH} />
    </svg>
  );

  // Announced rather than shown only as a colour change.
  const announcement = (
    <span className="sr-only" aria-live="polite">
      {copied ? `${value} copied to clipboard` : ''}
    </span>
  );

  if (variant !== 'inline') {
    return (
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy ${label ? `${label} ` : ''}${value}`}
        className={cx('group flex h-10 items-center gap-2.5', className)}
      >
        {label ? <span className="label-caps">{label}</span> : null}
        <span className="font-mono text-[15px] font-semibold tabular-nums text-ink-primary">{value}</span>
        {glyph}
        {announcement}
      </button>
    );
  }

  // `tap` rather than padding: this sits in a table row where the address has to
  // stay on the baseline its column shares, and padding would push the row
  // taller to buy a thumb-sized target that an invisible overlay gets for free.
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${value}`}
      title="Copy"
      className={cx(
        'tap group relative inline-flex items-center gap-2 font-mono text-sm tabular-nums text-ink-mono transition-colors hover:text-ink-primary',
        className,
      )}
    >
      {value}
      {glyph}
      {announcement}
    </button>
  );
}

/* ------------------------------------------------------------------ headings */

function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex h-10 items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-ink-tertiary hover:text-ink-primary"
    >
      <svg className="h-[15px] w-[15px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M19 12H5M11 18l-6-6 6-6" />
      </svg>
      {label}
    </Link>
  );
}

/**
 * The top of a page: one display headline, the facts about it set small on the
 * same baseline, and the action hard right. The 2px rule under it is the same
 * rule that divides every section below, so the heading reads as the first
 * section rather than as furniture above them.
 */
export function PageHeading({
  eyebrow,
  title,
  note,
  action,
  back,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  /** The line that sits beside the headline — a count, a plan, a description. */
  note?: ReactNode;
  action?: ReactNode;
  back?: { to: string; label: string };
  className?: string;
}) {
  return (
    <header className={className}>
      {back ? <BackLink to={back.to} label={back.label} /> : null}
      {eyebrow ? <p className="label-caps mb-3">{eyebrow}</p> : null}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 border-b-2 border-rule-strong pb-4">
        <h1 className="text-[clamp(38px,4.4vw,54px)] font-black leading-none tracking-display text-ink-primary">
          {title}
        </h1>
        {note ? <div className="pb-[7px] text-[14px] font-medium text-ink-tertiary">{note}</div> : null}
        {action ? <div className="ml-auto pb-px">{action}</div> : null}
      </div>
    </header>
  );
}

/** @deprecated `PageHeading` is the same heading with the design's proportions. */
export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return <PageHeading eyebrow={eyebrow} title={title} note={description} action={action} className="mb-8" />;
}

/**
 * A section inside a page. Smaller than a page heading and separated by the same
 * 2px rule, because the only thing that distinguishes a section from a page here
 * is the size of its type.
 */
export function SectionHeader({
  title,
  note,
  action,
  index,
  id,
  className,
}: {
  title: ReactNode;
  note?: ReactNode;
  action?: ReactNode;
  /** The red mono numeral for a numbered form section. */
  index?: number | string;
  id?: string;
  className?: string;
}) {
  return (
    <div className={cx('flex flex-wrap items-end gap-x-5 gap-y-2 border-b-2 border-rule-strong pb-3.5', className)}>
      {index === undefined ? null : <IndexNumeral value={index} className="pb-1.5" />}
      <h2 id={id} className="text-[30px] font-black leading-none tracking-display text-ink-primary">
        {title}
      </h2>
      {note ? <span className="pb-1 text-xs text-ink-tertiary">{note}</span> : null}
      {action ? <div className="ml-auto pb-px">{action}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------- compat shims */

/**
 * A recessed panel. There are no cards in this system — depth is rules — so this
 * is only the dark inset field (`ground-panel`) that a banner or an input group
 * sits on.
 */
export function Card({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={cx('bg-ground-panel', className)}>
      {children}
    </div>
  );
}

/** @deprecated Use `SectionHeader`. Kept so the pre-redesign pages still compile. */
export function CardHeader({
  title,
  description,
  action,
  separated = false,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  separated?: boolean;
}) {
  return (
    <div className={cx('flex flex-wrap items-start justify-between gap-x-4 gap-y-3 py-4', separated && 'rule-row')}>
      <div className="min-w-0">
        <h2 className="text-lg font-extrabold tracking-display text-ink-primary">{title}</h2>
        {description ? <p className="mt-1.5 max-w-prose text-sm text-ink-secondary">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/* --------------------------------------------------------- form controls */

/**
 * Underlined, not boxed. A field on this ground is drawn as the line you type
 * on: a border on four sides makes a form look like a stack of buttons, and the
 * dense firewall grid then has forty of them.
 *
 * `filled` is the second treatment the design uses, for controls packed into a
 * grid where forty underlines would be forty stripes.
 */
type ControlVariant = 'underline' | 'filled';

function controlClasses(variant: ControlVariant, invalid: boolean | undefined): string {
  const base =
    'w-full min-w-0 text-ink-primary transition-colors placeholder:text-ink-disabled ' +
    'disabled:cursor-not-allowed disabled:text-ink-disabled';
  if (variant === 'filled') {
    return cx(
      base,
      'bg-ground-panel px-3',
      // The invalid mark is a bar down the leading edge: in a grid this row has
      // to be findable without reading it, and a red outline on a borderless
      // control is a box appearing out of nowhere.
      invalid ? 'border-l-[3px] border-accent' : 'border-l-[3px] border-transparent',
    );
  }
  return cx(
    base,
    'border-b-2 bg-transparent px-0',
    invalid ? 'border-accent' : 'border-rule-control hover:border-ink-tertiary',
  );
}

export function Input({
  invalid,
  variant = 'underline',
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; variant?: ControlVariant }) {
  return (
    <input
      {...rest}
      aria-invalid={invalid ? true : undefined}
      className={cx(controlClasses(variant, invalid), 'h-10 font-mono text-sm tabular-nums', className)}
    />
  );
}

/**
 * The textarea is the SSH-key field and nothing else so far, which is why it is
 * the panel treatment with a bar down the leading edge rather than an underline:
 * a key is four lines of one value, and an underline four lines down would read
 * as the last line being the field.
 */
export function Textarea({
  invalid,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      {...rest}
      aria-invalid={invalid ? true : undefined}
      className={cx(
        'w-full min-w-0 resize-y bg-ground-panel px-4 py-3.5 font-mono text-xs leading-relaxed text-ink-primary',
        'placeholder:text-ink-disabled disabled:cursor-not-allowed disabled:text-ink-disabled',
        'border-l-[3px]',
        invalid ? 'border-accent' : 'border-rule-strong',
        className,
      )}
    />
  );
}

const CHEVRON = (
  <svg
    className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/**
 * A native `<select>`: the phone gets its own picker, which during an incident is
 * worth more than a bespoke listbox. Only the chevron is ours, so the control
 * does not fall back to the platform's grey triangle on a dark field.
 */
export function Select({
  invalid,
  variant = 'filled',
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean; variant?: ControlVariant }) {
  return (
    <span className={cx('relative block text-ink-tertiary', className)}>
      <select
        {...rest}
        aria-invalid={invalid ? true : undefined}
        className={cx(controlClasses(variant, invalid), 'h-10 cursor-pointer appearance-none pr-8 text-sm font-medium')}
      >
        {children}
      </select>
      {CHEVRON}
    </span>
  );
}

/** The select on the paper field — the image picker in the reinstall dialog. */
export function PaperSelect({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <span className={cx('relative block text-paper-ink', className)}>
      <select
        {...rest}
        className="h-11 w-full min-w-0 cursor-pointer appearance-none border-2 border-paper-ink bg-transparent pl-2.5 pr-8 text-[14px] font-semibold text-paper-ink"
      >
        {children}
      </select>
      {CHEVRON}
    </span>
  );
}

export function FormField({
  label,
  hint,
  error,
  htmlFor,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('min-w-0', className)}>
      <label htmlFor={htmlFor} className="label-caps mb-2 block">
        {label}
      </label>
      {children}
      {error ? (
        <p className={cx('mt-2.5 text-xs font-semibold', ACCENT_INK)}>{error}</p>
      ) : hint ? (
        <p className="mt-2.5 text-xs text-ink-tertiary">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * A one-of-N switch, drawn as an underline rather than a filled pill: it sits at
 * the end of a section header, and a row of filled pills there would out-weigh
 * the headline they belong to.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ value: T; label: ReactNode; count?: number }>;
  label: string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cx('flex flex-wrap', className)}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            className={cx(
              'inline-flex h-10 items-center gap-1.5 border-b-2 px-3.5 text-xs font-bold uppercase tracking-[0.04em] transition-colors',
              active
                ? 'border-accent text-ink-primary'
                : 'border-transparent text-ink-tertiary hover:text-ink-secondary',
            )}
          >
            {option.label}
            {option.count === undefined ? null : (
              <span className="font-mono text-2xs tabular-nums text-ink-tertiary">{option.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A switch for a setting that applies the moment it moves.
 *
 * The control is 40×40 and the *track* is 36×20 inside it. A switch drawn at its
 * own visual height is a 20px target, which is half of what a thumb needs; every
 * previous attempt to fix that made the switch physically bigger, which is not
 * the same thing and looks like a toy. The button carries the height, the track
 * is centred in it, and the hit area is honest.
 *
 * `busy` keeps it pressed-but-inert while a request is in flight rather than
 * letting the knob snap back and forth on a slow connection.
 */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  busy,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled || busy}
      onClick={() => onChange(!checked)}
      className="group inline-flex h-10 w-10 shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span
        className={cx(
          'relative block h-5 w-9 border transition-colors',
          checked ? 'border-paper bg-paper' : 'border-rule-control bg-rule-strong',
        )}
      >
        <span
          className={cx(
            'absolute top-[2px] block h-3.5 w-3.5 transition-all',
            checked ? 'left-[18px] bg-ground' : 'left-[2px] bg-ink-secondary',
          )}
        />
        {busy ? <Spinner className="absolute inset-0 m-auto h-3 w-3" tone={checked ? 'paper' : 'dark'} /> : null}
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------- overlay */

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * The modal is a paper panel with a 6px accent edge along the top.
 *
 * A sheet on a phone, a centred dialog above it. `busy` blocks Escape and the
 * backdrop while a request is in flight: a half-sent delete whose dialog has
 * vanished leaves somebody unsure whether they deleted their server or not.
 */
export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  description,
  children,
  footer,
  busy,
  tone = 'default',
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** The small line above the headline, e.g. "This cannot be undone". */
  eyebrow?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  busy?: boolean;
  tone?: 'default' | 'danger';
  labelledBy?: string;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);

    // The page behind must not scroll under the sheet — on a phone that is how
    // you lose your place in a long list while confirming one action.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, busy]);

  if (!open) return null;

  // Tab stays inside the dialog: the controls behind it are inert, and tabbing
  // onto them would put the keyboard somewhere the eye cannot follow.
  const trapTab = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!nodes || nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6">
      <div className="absolute inset-0 bg-ground/80" onClick={() => !busy && onClose()} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId}
        tabIndex={-1}
        onKeyDown={trapTab}
        className={cx(
          'paper-field animate-rise relative max-h-[92vh] w-full overflow-y-auto border-t-[6px] sm:max-w-[540px]',
          tone === 'danger' ? 'border-accent' : 'border-paper-ink',
        )}
      >
        <div className="px-7 pb-[22px] pt-[26px]">
          {eyebrow ? (
            <p className={cx('mb-3 text-2xs font-bold uppercase tracking-caps', ACCENT_INK_PAPER)}>{eyebrow}</p>
          ) : null}
          <h2 id={titleId} className="text-[30px] font-black leading-none tracking-display text-paper-ink">
            {title}
          </h2>
          {description ? (
            <p className="mt-3 text-[14px] leading-relaxed text-paper-secondary">{description}</p>
          ) : null}
          {children ? <div className="mt-5">{children}</div> : null}
        </div>
        {footer ? (
          <div className={cx('flex flex-wrap justify-end gap-0.5 border-t px-7 py-[18px]', PAPER_RULE)}>{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Two columns: what a destructive action keeps, and what it takes away.
 *
 * The question somebody has with their hand on a reinstall button is not "is
 * this permanent" — the headline already said so. It is "which of the things I
 * set up will I have to set up again". A sentence answers that badly and a list
 * answers it at a glance, which is also why the kept column comes first: the
 * reassuring half is the half that stops people abandoning the action and
 * opening a ticket instead.
 */
export function KeptLost({
  heading,
  tone,
  items,
}: {
  heading: string;
  tone: 'kept' | 'lost';
  items: readonly string[];
}) {
  const lost = tone === 'lost';
  return (
    <div className="bg-paper px-5 py-4">
      <p className={cx('mb-2.5 text-2xs font-bold uppercase tracking-caps', lost ? ACCENT_INK_PAPER : 'text-paper-tertiary')}>
        {heading}
      </p>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-[13px] leading-snug text-paper-secondary">
            <svg
              className={cx('mt-[3px] h-3.5 w-3.5 shrink-0', lost ? ACCENT_INK_PAPER : 'text-paper-tertiary')}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              {lost ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M20 6L9 17l-5-5" />}
            </svg>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The hairline between the two columns above, and around them. */
export const PAPER_RULE_BORDER = 'border-paper-ink/20 bg-paper-ink/20';

/**
 * The destructive confirmation: reinstall and delete, and nothing else.
 *
 * The hostname has to be typed out in full before the action exists at all.
 * That is not friction for its own sake. Both of these destroy a disk with no
 * backup behind it, and the one thing that reliably stops the wrong machine
 * being deleted is being made to write down which machine it is.
 *
 * The step is worded as an instruction rather than as a label, because the
 * earlier version was read as a heading: a caps line saying "TYPE test-srv TO
 * CONFIRM" sitting above a field whose placeholder was also "test-srv" looks
 * like a filled-in form, and people clicked a disabled button wondering why
 * nothing happened.
 */
export function ConfirmModal({
  open,
  onClose,
  title,
  description,
  hostname,
  confirmLabel,
  onConfirm,
  busy,
  blocked,
  eyebrow = 'This cannot be undone',
  children,
  cancelLabel = 'Cancel',
  confirmPrompt,
  confirmPlaceholder = 'Type the name here',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Typed back exactly before the action arms. */
  hostname: string;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
  /**
   * Something else is wrong, so the action must not arm even with the name
   * typed correctly. Distinct from `busy`, which means a request is in flight
   * and therefore also blocks Escape and shows a spinner.
   */
  blocked?: boolean;
  eyebrow?: ReactNode;
  /** Anything the action needs first, such as the image picker on a reinstall. */
  children?: ReactNode;
  cancelLabel?: string;
  /** Replaces the default “type the hostname” sentence. The boxed name still follows. */
  confirmPrompt?: ReactNode;
  confirmPlaceholder?: string;
}) {
  const [typed, setTyped] = useState('');
  const fieldId = useId();

  // A dialog that reopens holding the last hostname would arm itself before it
  // was read.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  // Trimmed only at the ends: a pasted hostname often arrives with a space, and
  // no amount of trimming turns one hostname into another.
  const armed = typed.trim() === hostname && hostname !== '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      tone="danger"
      eyebrow={eyebrow}
      title={title}
      description={description}
      footer={
        <>
          <PaperButton variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </PaperButton>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!armed || busy || blocked}
            className={cx(
              'inline-flex h-11 select-none items-center justify-center gap-2.5 whitespace-nowrap border-2 px-[18px] text-[14px] font-bold transition-colors',
              armed && !busy && !blocked
                ? 'border-accent bg-accent text-paper'
                : cx('cursor-not-allowed bg-transparent text-paper-tertiary', PAPER_RULE),
            )}
          >
            {busy ? <Spinner className="h-3.5 w-3.5" tone="paper" /> : null}
            {confirmLabel}
          </button>
        </>
      }
    >
      {children}

      {/* The confirmation step, spelled out.
          It used to be a caps label reading "TYPE test-srv TO CONFIRM" above an
          empty line, and people read it as another heading rather than as an
          instruction: the hostname looked like part of the label, and the input
          under it looked like it was already filled in because the placeholder
          was the same string. Both are fixed here. The ask is a sentence, the
          name to type is a boxed token, and the field starts empty with a
          neutral prompt so nothing looks pre-filled. */}
      <div className={cx('mt-6 border-t pt-5', PAPER_RULE)}>
        <label htmlFor={fieldId} className="block text-[14px] leading-relaxed text-paper-secondary">
          {confirmPrompt ?? (
            <>
              To confirm, type the server&apos;s name{' '}
              <span className="mx-0.5 inline-block border border-paper-ink/25 bg-paper-ink/[0.06] px-1.5 py-px font-mono text-[13px] font-bold text-paper-ink">
                {hostname}
              </span>{' '}
              in the box below.
            </>
          )}
        </label>

        <div className="relative mt-2.5">
          <input
            id={fieldId}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={confirmPlaceholder}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={busy}
            aria-describedby={`${fieldId}-hint`}
            className={cx(
              'h-12 w-full border-b-2 bg-transparent pr-9 font-mono text-lg font-bold text-paper-ink',
              'placeholder:font-sans placeholder:text-[15px] placeholder:font-normal placeholder:text-paper-tertiary/70',
              armed ? 'border-accent' : PAPER_RULE,
            )}
          />
          {/* A tick the moment it matches, so nobody hunts for a typo in a name
              they have already typed correctly. */}
          {armed ? (
            <svg
              className={cx('pointer-events-none absolute right-1 top-3 h-5 w-5', ACCENT_INK_PAPER)}
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          ) : null}
        </div>

        <p id={`${fieldId}-hint`} className="mt-2 text-[12px] leading-relaxed text-paper-tertiary">
          {armed
            ? 'That matches. The button below is now active.'
            : 'This is here so the wrong server cannot be picked by accident. Nothing happens until the name matches.'}
        </p>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------- brand */

/**
 * The mark. A real image rather than a redrawn SVG: it is the customer's own
 * logo and the one place in this system where a radius is allowed, because the
 * mark is a circle.
 *
 * Served from this app rather than hotlinked from vgncloud.com/assets/logo.png,
 * even though that is where it comes from. This panel is what somebody opens
 * when something is wrong, and a logo fetched across an origin fails exactly
 * when the storefront is the thing that is down. The file here is a copy of
 * that one; if the brand mark changes, copy it across again.
 */
export function LogoMark({ className }: { className?: string }) {
  return (
    <img
      src="/vgn-logo.png"
      alt=""
      aria-hidden="true"
      className={cx('block shrink-0 rounded-full', className ?? 'h-7 w-7')}
    />
  );
}

const LOGO_SIZES = {
  sm: { gap: 'gap-2.5', mark: 'h-7 w-7', word: 'text-[15px]' },
  md: { gap: 'gap-[11px]', mark: 'h-7 w-7', word: 'text-[16px]' },
  lg: { gap: 'gap-3', mark: 'h-10 w-10', word: 'text-xl' },
} as const;

export function Logo({
  className,
  markClassName,
  size = 'md',
}: {
  className?: string;
  markClassName?: string;
  size?: keyof typeof LOGO_SIZES;
}) {
  const s = LOGO_SIZES[size];
  return (
    <span className={cx('inline-flex items-center', s.gap, className)}>
      <LogoMark className={markClassName ?? s.mark} />
      <span className={cx('font-extrabold tracking-display text-ink-primary', s.word)}>VGNCloud</span>
    </span>
  );
}

/**
 * The whole screen, while we do not yet know enough to draw anything on it.
 * Three breathing squares rather than a spinner: this is a wait measured in
 * hundreds of milliseconds, and a spinner sets the expectation of seconds.
 */
export function FullPageLoader({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center gap-5 px-6" role="status" aria-live="polite">
      <LogoMark className="h-10 w-10" />
      <span className="flex gap-[5px]" aria-hidden="true">
        {[0, 0.3, 0.6].map((delay) => (
          <span
            key={delay}
            className="h-[7px] w-[7px] animate-breathe bg-accent"
            style={{ animationDelay: `${delay}s` }}
          />
        ))}
      </span>
      <span className="text-[14px] text-ink-secondary">{label}</span>
    </div>
  );
}

/* ----------------------------------------------------------------- failure */

/**
 * One place decides what a failure means, so six screens cannot disagree.
 *
 * Every case answers two questions, because an error that answers only the first
 * leaves somebody staring at it: what happened, and what to do about it. `hint`
 * is the second answer, and it is a different sentence per cause — "try again"
 * is not advice when the thing that failed is a session that ended.
 *
 * The distinction that matters: 401 and 404 are answers, not faults — the
 * session ended, or this is not this account's to see — and offering a retry for
 * either would just loop somebody through the same wall.
 */
interface ErrorInfo {
  title: string;
  message: string;
  hint: string | null;
  retryable: boolean;
}

function describeError(error: unknown, what: string): ErrorInfo {
  if (error instanceof ApiError) {
    if (error.status === 0) {
      return {
        title: 'No connection',
        message: `${error.message} ${what} could not be loaded.`,
        hint: 'Your servers are unaffected. This is the connection between your browser and us, so check the network and try again.',
        retryable: true,
      };
    }
    if (error.status === 401) {
      return {
        title: 'Signed out',
        message: 'This session has ended.',
        hint: 'Sign in again and you will come straight back to this page.',
        retryable: false,
      };
    }
    if (error.status === 403 || error.status === 404) {
      return {
        title: 'Not available',
        message: `${what} is not available to this account.`,
        hint: 'It may have been deleted, or the link may belong to a different account. Nothing has been lost.',
        retryable: false,
      };
    }
    if (error.status === 429) {
      const wait = error.retryAfterSeconds;
      return {
        title: 'Slow down',
        message: error.message,
        hint:
          wait === null
            ? 'Wait a moment before trying again.'
            : `Try again in ${wait} second${wait === 1 ? '' : 's'}.`,
        retryable: true,
      };
    }
    if (error.status >= 500) {
      return {
        title: `Could not load ${what}`,
        message: error.message,
        hint: 'This one is on our side. Trying again often works; if it does not, it is worth contacting support.',
        retryable: true,
      };
    }
    return { title: `Could not load ${what}`, message: error.message, hint: null, retryable: true };
  }
  return {
    title: `Could not load ${what}`,
    message: error instanceof Error && error.message !== '' ? error.message : 'Something went wrong.',
    hint: 'Trying again is safe. Nothing on your server was changed.',
    retryable: true,
  };
}

/** The message alone, for a failure shown inside a dialog or beside a control. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error && error.message !== '' ? error.message : 'Something went wrong.';
}

export function ErrorPanel({ error, what = 'this page', onRetry }: { error: unknown; what?: string; onRetry?: () => void }) {
  const info = describeError(error, what);
  return (
    <Alert tone="error" title={`${info.title}.`}>
      <p>{info.message}</p>
      {info.hint ? <p className="mt-1.5 text-ink-tertiary">{info.hint}</p> : null}
      {onRetry && info.retryable ? (
        <div className="mt-3.5">
          <Button size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </Alert>
  );
}

/**
 * A failure that took the whole screen with it, rather than one panel on it.
 * Left-aligned and given room: at this point the page has nothing else on it and
 * this is the only thing to read.
 */
export function ErrorState({
  error,
  what = 'this page',
  onRetry,
  children,
}: {
  error: unknown;
  what?: string;
  onRetry?: () => void;
  /** A second way out — back to the list, usually — beside the retry. */
  children?: ReactNode;
}) {
  const info = describeError(error, what);
  return (
    <div className="max-w-[620px] py-12">
      <h2 className="text-2xl font-black tracking-display text-ink-primary">{info.title}</h2>
      <p className="mt-3 text-base leading-relaxed text-ink-secondary">{info.message}</p>
      {info.hint ? <p className="mt-2 text-sm leading-relaxed text-ink-tertiary">{info.hint}</p> : null}
      <div className="mt-7 flex flex-wrap gap-0.5">
        {onRetry && info.retryable ? (
          <Button variant="primary" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        {children}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- loading */

export interface Resource<T> {
  data: T | null;
  error: unknown;
  /** Nothing to draw yet — this is what a skeleton is for. */
  loading: boolean;
  /** A reload over data already on screen. Never blank what is showing. */
  refreshing: boolean;
  reload: () => void;
}

/**
 * One request, with the three behaviours every screen here needs and nobody
 * should have to write twice:
 *
 *   - the in-flight request is aborted when the filter changes or the page
 *     unmounts, so a slow first response cannot overwrite a fast second one;
 *   - a failed reload keeps the last good data on screen rather than replacing a
 *     working list with a red box, which during an incident is the difference
 *     between a blip and a blackout;
 *   - `reload` is stable, so a polling effect can depend on it without re-arming
 *     its timer on every render.
 *
 * `load` must be stable too (useCallback): it is the dependency that decides
 * when to re-fetch.
 */
export function useResource<T>(load: (signal: AbortSignal) => Promise<T>): Resource<T> {
  const [state, setState] = useState<{ data: T | null; error: unknown }>({ data: null, error: null });
  const [pending, setPending] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setPending(true);

    load(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ data, error: null });
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setState((prev) => ({ data: prev.data, error: err }));
      })
      .finally(() => {
        if (!controller.signal.aborted) setPending(false);
      });

    return () => controller.abort();
  }, [load, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const hasData = state.data !== null;

  return {
    data: state.data,
    error: state.error,
    loading: pending && !hasData,
    refreshing: pending && hasData,
    reload,
  };
}
